import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CurrencyDollarIcon, 
  ClockIcon, 
  DocumentTextIcon, 
  ArrowPathIcon,
  ChartBarIcon,
  DocumentPlusIcon
} from '@heroicons/react/24/outline';
import Tooltip from '../../components/ui/Tooltip';
import LogTimeButton from '../../components/billing/LogTimeButton';
import DataTable from '../../components/common/DataTable';
import api from '../../utils/api';
import { formatCurrency, formatHours } from '../../utils';

const BillingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingData, setBillingData] = useState({
    stats: [],
    clientSummary: [],
    recentInvoices: [],
    recentTimeEntries: []
  });

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      
      // Fetch client billing summary with null check
      const clientSummaryResp = await api.get('/api/billing/client-summary/');
      const clientSummaryResponse = clientSummaryResp.data || clientSummaryResp;
      const clientSummary = (clientSummaryResponse?.results || []).map(client => ({
        ...client,
        unbilled_hours: client.unbilled_hours || 0,
        unbilled_amount: client.unbilled_amount || 0,
        outstanding_invoices: client.outstanding_invoices || 0
      }));
      
      // Fetch recent invoices with null check
      const invoicesResp = await api.get('/api/billing/invoices/?limit=5');
      const invoicesResponse = invoicesResp.data || invoicesResp;
      const recentInvoices = invoicesResponse?.results || [];
      
      // Fetch recent time entries with null check
      const timeEntriesResp = await api.get('/api/billing/time-entries/?limit=5');
      const timeEntriesResponse = timeEntriesResp.data || timeEntriesResp;
      const recentTimeEntries = timeEntriesResponse?.results || [];
      
      // Calculate stats from the data
      const totalUnbilled = clientSummary.reduce((sum, client) => sum + client.unbilled_amount, 0);
      const totalOutstanding = clientSummary.reduce((sum, client) => sum + client.outstanding_invoices, 0);
      const totalUnbilledHours = clientSummary.reduce((sum, client) => sum + client.unbilled_hours, 0);
      
      const stats = [
        {
          name: 'Unbilled Hours',
          value: formatHours(totalUnbilledHours),
          icon: ClockIcon,
          color: 'bg-blue-500',
        },
        {
          name: 'Unbilled Amount',
          value: formatCurrency(totalUnbilled),
          icon: CurrencyDollarIcon,
          color: 'bg-green-500',
        },
        {
          name: 'Outstanding Invoices',
          value: formatCurrency(totalOutstanding),
          icon: DocumentTextIcon,
          color: 'bg-amber-500',
        },
        {
          name: 'Collected This Month',
          value: formatCurrency(0),
          icon: ChartBarIcon,
          color: 'bg-violet-500',
        }
      ];
      
      setBillingData({
        stats,
        clientSummary,
        recentInvoices,
        recentTimeEntries
      });
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError('Failed to load billing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Billing Dashboard</h2>
        <div className="flex space-x-3">
          <LogTimeButton 
            refreshCallback={fetchBillingData}
            buttonClassName="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md shadow-sm hover:bg-primary-700"
            iconClassName="h-4 w-4 mr-2"
          />
          <Link 
            to="/billing/invoices/new" 
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md shadow-sm hover:bg-primary-700"
          >
            <DocumentPlusIcon className="h-4 w-4 mr-2" /> 
            New Invoice
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {billingData.stats.map(stat => (
          <Tooltip 
            key={stat.name} 
            content={stat.name}
            position="top"
          >
            <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg overflow-hidden cursor-help">
              <div className="p-5">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 rounded-md p-3 ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{stat.name}</dt>
                    <dd className="text-2xl font-semibold text-gray-900 dark:text-white">{stat.value}</dd>
                  </div>
                </div>
              </div>
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Client Billing Summary */}
      <div className="mb-8">
        <DataTable
          columns={[
            {
              key: 'name',
              label: 'Client',
              className: 'font-medium text-gray-900 dark:text-white',
            },
            {
              key: 'unbilled_hours',
              label: 'Unbilled Hours',
              headerClassName: 'text-right',
              className: 'text-right text-gray-500 dark:text-gray-300',
              render: (row) => formatHours(row.unbilled_hours),
              sortValue: (row) => row.unbilled_hours,
            },
            {
              key: 'unbilled_amount',
              label: 'Unbilled Amount',
              headerClassName: 'text-right',
              className: 'text-right text-gray-500 dark:text-gray-300',
              render: (row) => formatCurrency(row.unbilled_amount),
              sortValue: (row) => row.unbilled_amount,
            },
            {
              key: 'outstanding_invoices',
              label: 'Outstanding Invoices',
              headerClassName: 'text-right',
              className: 'text-right text-gray-500 dark:text-gray-300',
              render: (row) => formatCurrency(row.outstanding_invoices),
              sortValue: (row) => row.outstanding_invoices,
            },
            {
              key: 'actions',
              label: 'Actions',
              sortable: false,
              headerClassName: 'text-center',
              className: 'text-center',
              render: (row) => (
                <Tooltip content={`Create invoice for ${row.name}`} position="left">
                  <Link
                    to={`/billing/invoices/new?client=${row.id}`}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 text-sm font-medium"
                    aria-label={`Create invoice for ${row.name}`}
                  >
                    Create Invoice
                  </Link>
                </Tooltip>
              ),
            },
          ]}
          data={billingData.clientSummary}
          searchPlaceholder="Search clients…"
          searchKeys={['name']}
          defaultSort={{ key: 'unbilled_amount', direction: 'desc' }}
          emptyMessage="No billing data available"
          headerRight={
            <button
              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center"
              onClick={() => window.location.reload()}
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" /> Refresh
            </button>
          }
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Recent Invoices */}
        <DataTable
          columns={[
            {
              key: 'invoice_number',
              label: 'Invoice',
              render: (row) => (
                <Link to={`/billing/invoices/${row.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                  {row.invoice_number}
                </Link>
              ),
            },
            {
              key: 'client',
              label: 'Client',
              render: (row) =>
                row.client_detail?.full_name || `${row.client_detail?.first_name || ''} ${row.client_detail?.last_name || ''}`.trim() || 'Unknown Client',
            },
            {
              key: 'total_amount',
              label: 'Amount',
              headerClassName: 'text-right',
              className: 'text-right',
              render: (row) => formatCurrency(row.total_amount),
              sortValue: (row) => parseFloat(row.total_amount) || 0,
            },
            {
              key: 'status',
              label: 'Status',
              headerClassName: 'text-center',
              className: 'text-center',
              render: (row) => (
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  row.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  row.status === 'overdue' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                  'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                }`}>
                  {row.status_display}
                </span>
              ),
            },
          ]}
          data={billingData.recentInvoices}
          searchable={false}
          pageSize={5}
          pageSizeOptions={[5]}
          defaultSort={{ key: 'invoice_number', direction: 'desc' }}
          emptyMessage="No recent invoices"
          emptyIcon={DocumentTextIcon}
          headerRight={
            <Link to="/billing/invoices" className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 text-sm font-medium">
              View All
            </Link>
          }
        />

        {/* Recent Time Entries */}
        <DataTable
          columns={[
            {
              key: 'description',
              label: 'Description',
              className: 'font-medium text-gray-900 dark:text-white',
              render: (row) => (
                <div className="max-w-[200px] truncate">{row.description}</div>
              ),
            },
            {
              key: 'case',
              label: 'Case',
              render: (row) => row.case_detail?.title || 'Unknown Case',
            },
            {
              key: 'hours',
              label: 'Hours',
              headerClassName: 'text-right',
              className: 'text-right',
              render: (row) => formatHours(row.hours),
              sortValue: (row) => parseFloat(row.hours) || 0,
            },
            {
              key: 'amount',
              label: 'Amount',
              headerClassName: 'text-right',
              className: 'text-right',
              render: (row) => formatCurrency(row.amount),
              sortValue: (row) => parseFloat(row.amount) || 0,
            },
          ]}
          data={billingData.recentTimeEntries}
          searchable={false}
          pageSize={5}
          pageSizeOptions={[5]}
          defaultSort={{ key: 'hours', direction: 'desc' }}
          emptyMessage="No recent time entries"
          emptyIcon={ClockIcon}
          headerRight={
            <Link to="/billing/time-entries" className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 text-sm font-medium">
              View All
            </Link>
          }
        />
      </div>
    </div>
  );
};

export default BillingDashboard;
